import { Button, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Share2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { redirectToFacebookOAuth } from "@/modules/facebook/presentation/api/client";
import {
  useCheckFacebookPageToken,
  useConnectFacebookPage,
  useDisconnectFacebookPage,
  useFacebookPagesQuery,
  useFacebookSetupQuery,
  useRefreshFacebookTokens,
} from "@/modules/facebook/presentation/api/queries";
import { FacebookDevSetupAlert } from "@/modules/facebook/presentation/ui/FacebookDevSetupAlert";
import { FacebookPageCard } from "@/modules/facebook/presentation/ui/FacebookPageCard";
import { FacebookTokenAlert } from "@/modules/facebook/presentation/ui/FacebookTokenAlert";
import { WebhookActivityPanel } from "@/modules/facebook/presentation/ui/WebhookActivityPanel";
import { QueryState } from "@/shared/ui/QueryState";

type FacebookConnectSearch = {
  facebook?: string;
  reason?: string;
  pages?: string;
  missing?: string;
  omitted?: string;
};

export function FacebookConnectPage() {
  const search = useSearch({ from: "/app/facebook" }) as FacebookConnectSearch;
  const nav = useNavigate({ from: "/app/facebook" });
  const oauthToastShown = useRef(false);

  const setupQuery = useFacebookSetupQuery();
  const pagesQuery = useFacebookPagesQuery();
  const { refetch: refetchPages } = pagesQuery;
  const connectPage = useConnectFacebookPage();
  const disconnectPage = useDisconnectFacebookPage();
  const checkToken = useCheckFacebookPageToken();
  const refreshTokens = useRefreshFacebookTokens();

  useEffect(() => {
    if (!search.facebook || oauthToastShown.current) return;
    oauthToastShown.current = true;

    if (search.facebook === "connected") {
      const count = search.pages ? Number(search.pages) : 0;
      toast.success("ເຊື່ອມຕໍ່ Facebook ສໍາເລັດ", {
        description:
          count > 0
            ? `ດຶງ Page ມາ ${count} ໜ້າ — ກະລຸນາກົດ «ເຊື່ອມ webhook» ສຳລັບແຕ່ລະ Page`
            : "ກວດສອບລາຍການ Page ດ້ານລຸ່ມ",
      });
      void refetchPages();
    } else if (search.facebook === "permission_required") {
      const missing = search.missing?.split(",").filter(Boolean) ?? [];
      toast.error("ສິດ Facebook ຍັງບໍ່ຄົບ", {
        description:
          missing.length > 0
            ? `ກະລຸນາເຊື່ອມໃໝ່ ແລະ ອະນຸຍາດ: ${missing.join(", ")}`
            : "ກະລຸນາເຊື່ອມ Facebook ໃໝ່ ແລະ ອະນຸຍາດສິດທັງໝົດ",
      });
      void refetchPages();
    } else if (search.facebook === "page_access_required") {
      const omitted = search.omitted?.split(",").filter(Boolean) ?? [];
      toast.error("Facebook ຍັງບໍ່ໄດ້ອະນຸຍາດບາງ Page", {
        description:
          omitted.length > 0
            ? `ກະລຸນາເຊື່ອມໃໝ່ ແລະ ເລືອກ Page ເຫຼົ່ານີ້ໃນໜ້າ Facebook: ${omitted.join(", ")}`
            : "ກະລຸນາເຊື່ອມໃໝ່ ແລະ ເລືອກ Page ທີ່ຕ້ອງການໃນໜ້າ Facebook",
      });
      void refetchPages();
    } else if (search.facebook === "rate_limited") {
      toast.error("Facebook ຈຳກັດຈຳນວນຄຳຂໍຊົ່ວຄາວ", {
        description: "ກະລຸນາລໍຖ້າ ແລ້ວລອງເຊື່ອມ Facebook ໃໝ່ພາຍຫຼັງ",
      });
    } else if (search.facebook === "error") {
      const reason = search.reason ?? "oauth_failed";
      const isRedirectBlocked =
        reason.includes("redirect") ||
        reason.includes("URL Blocked") ||
        reason === "access_denied";
      toast.error("ເຊື່ອມຕໍ່ Facebook ລົ້ມເຫຼວ", {
        description: isRedirectBlocked
          ? `${reason} — ກວດ Valid OAuth Redirect URIs ໃນ Meta (ຫຼືໃຊ້ ngrok, ດູຄຳແນະນຳດ້ານເທິງ)`
          : reason,
      });
    }

    nav({
      search: {
        facebook: undefined,
        reason: undefined,
        pages: undefined,
        missing: undefined,
        omitted: undefined,
      },
      replace: true,
    });
  }, [
    search.facebook,
    search.pages,
    search.reason,
    search.missing,
    search.omitted,
    nav,
    refetchPages,
  ]);

  const pages = pagesQuery.data?.pages ?? [];
  const activePages = pages.filter((p) => p.status !== "disconnected");
  const [oauthStarting, setOauthStarting] = useState(false);

  const handleConnectFacebook = async () => {
    setOauthStarting(true);
    try {
      await redirectToFacebookOAuth("/app/facebook");
    } catch (e) {
      const message = e instanceof Error ? e.message : "oauth_start_failed";
      toast.error("ເລີ່ມ OAuth ລົ້ມເຫຼວ", { description: message });
      setOauthStarting(false);
    }
  };

  return (
    <>
      <Header />

      <Main>
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">
              ເຊື່ອມຕໍ່ Facebook
            </h2>
            <p className="text-muted-foreground">
              ເຂົ້າສູ່ລະບົບ Facebook ເພື່ອດຶງ Page ທີ່ທ່ານຈັດການ ແລະ subscribe webhook ສຳລັບ
              comment / Messenger
            </p>
          </div>
          <Button
            type="button"
            disabled={setupQuery.data?.configured === false || oauthStarting}
            onClick={() => void handleConnectFacebook()}
          >
            <Share2 className="size-4" />
            {oauthStarting ? "ກຳລັງເປີດ Facebook..." : "ເຊື່ອມຕໍ່ບັນຊີ Facebook"}
          </Button>
        </div>

        <FacebookDevSetupAlert setup={setupQuery.data} />

        <FacebookTokenAlert
          pages={pages}
          refreshing={refreshTokens.isPending}
          reconnecting={oauthStarting}
          onRefresh={() => refreshTokens.mutate()}
          onReconnect={() => void handleConnectFacebook()}
        />

        <WebhookActivityPanel />

        <QueryState
          result={pagesQuery}
          isEmpty={pages.length === 0}
          title="ຍັງບໍ່ມີ Page"
          description="ກົດປຸ່ມ «ເຊື່ອມຕໍ່ບັນຊີ Facebook» ເພື່ອເລີ່ມ OAuth ແລະດຶງ Page ທີ່ທ່ານມີສິດຈັດການ"
          emptyFallback={
            <div className="rounded-xl border border-dashed bg-card p-8 text-center">
              <p className="text-muted-foreground text-sm">
                ຫຼັງ OAuth ສຳເລັດ Page ຈະປາກົດທີ່ນີ້ — ຈາກນັ້ນກົດ «ເຊື່ອມ webhook» ແຕ່ລະ Page
                ເພື່ອຮັບ comment ແລະ Messenger
              </p>
              <Button
                type="button"
                className="mt-4"
                disabled={oauthStarting}
                onClick={() => void handleConnectFacebook()}
              >
                <Share2 className="size-4" />
                {oauthStarting ? "ກຳລັງເປີດ Facebook..." : "ເຊື່ອມຕໍ່ບັນຊີ Facebook"}
              </Button>
            </div>
          }
        >
          <div className="space-y-4">
            {activePages.length > 0 ? (
              <p className="text-muted-foreground text-sm">
                Page ທີ່ໃຊ້ງານ ({activePages.length})
              </p>
            ) : null}
            {activePages.map((page) => (
              <FacebookPageCard
                key={page.id}
                page={page}
                connecting={connectPage.isPending}
                disconnecting={disconnectPage.isPending}
                checkingToken={checkToken.isPending}
                onConnect={() => connectPage.mutate(page.id)}
                onReconnect={() => void handleConnectFacebook()}
                onDisconnect={() => disconnectPage.mutate(page.id)}
                onCheckToken={() => checkToken.mutate(page.id)}
                reconnecting={oauthStarting}
              />
            ))}

            {pages.some((p) => p.status === "disconnected") ? (
              <>
                <p className="pt-4 text-muted-foreground text-sm">
                  Page ທີ່ຕັດການເຊື່ອມແລ້ວ
                </p>
                {pages
                  .filter((p) => p.status === "disconnected")
                  .map((page) => (
                    <FacebookPageCard
                      key={page.id}
                      page={page}
                      connecting={connectPage.isPending}
                      disconnecting={disconnectPage.isPending}
                      checkingToken={checkToken.isPending}
                      onConnect={() => connectPage.mutate(page.id)}
                      onReconnect={() => void handleConnectFacebook()}
                      onDisconnect={() => disconnectPage.mutate(page.id)}
                      onCheckToken={() => checkToken.mutate(page.id)}
                      reconnecting={oauthStarting}
                    />
                  ))}
              </>
            ) : null}
          </div>
        </QueryState>
      </Main>
    </>
  );
}
